import type { Vault } from '@/types/vault'
import { getCuratorDisplayName } from '@/utils/curator'
import { getProtocolDisplayName } from '@/utils/protocol'

interface Props {
  vault: Vault
  pageUrl: string
  description?: string
  interestRate?: number
}

interface PropertyValue {
  '@type': 'PropertyValue'
  propertyID: string
  value: string | number | boolean
  name?: string
  description?: string
  identifier?: string
  unitText?: string
  validThrough?: string
}

const propertyValue = (
  propertyID: string,
  value: PropertyValue['value'],
  options: Omit<PropertyValue, '@type' | 'propertyID' | 'value'> = {}
): PropertyValue => ({
  '@type': 'PropertyValue',
  propertyID,
  value,
  ...options,
})

export const getVaultStructuredData = ({ vault, pageUrl, description, interestRate }: Props) => {
  const identifiers = [
    propertyValue('vaultId', vault.vaultId),
    propertyValue('vaultContract', vault.address),
    propertyValue('network', vault.network.networkCaip, { name: vault.network.name }),
    propertyValue('underlyingAsset', vault.asset.address, { name: vault.asset.symbol }),
    propertyValue('underlyingAssetCaip', vault.asset.assetCaip),
  ]

  if (vault.lpToken) {
    identifiers.push(
      propertyValue('lpToken', vault.lpToken.address, { name: vault.lpToken.symbol }),
      propertyValue('lpTokenCaip', vault.lpToken.tokenCaip)
    )
  }

  if (vault.curator) {
    identifiers.push(
      propertyValue('curator', vault.curator.name, { name: getCuratorDisplayName(vault.curator) }),
      ...(vault.curator.websiteUrl ? [propertyValue('curatorUrl', vault.curator.websiteUrl)] : [])
    )
  }

  const additionalProperties: PropertyValue[] = [propertyValue('isCorrupted', vault.isCorrupted)]

  for (const [feeType, fee] of Object.entries(vault.fees ?? {})) {
    if (fee !== undefined) {
      additionalProperties.push(propertyValue(feeType, fee, { unitText: 'decimal' }))
    }
  }

  for (const flag of vault.flags) {
    additionalProperties.push(
      propertyValue('flag', flag.content, {
        name: flag.severity,
        ...(flag.resolution && { description: flag.resolution }),
        validThrough: new Date(flag.endDate * 1000).toISOString(),
      })
    )
  }

  for (const reward of vault.rewards) {
    additionalProperties.push(
      propertyValue('rewardAsset', reward.asset.address, {
        name: reward.asset.symbol,
        identifier: reward.asset.assetCaip,
      })
    )
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    '@id': pageUrl,
    name: vault.name,
    url: pageUrl,
    identifier: identifiers,
    provider: {
      '@type': 'Organization',
      name: getProtocolDisplayName(vault.protocol),
    },
    ...(vault.tags.length > 0 && { keywords: vault.tags }),
    dateModified: new Date(vault.lastUpdateTimestamp * 1000).toISOString(),
    additionalProperty: additionalProperties,
    ...(description && { description }),
    ...(interestRate !== undefined && { interestRate }),
  }
}
